import { useSearchParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { coursesAPI } from '../../config/api'
import { motion } from 'framer-motion'
import ToastNotification from '../../components/ToastNotification'
import { QRCodeSVG } from 'qrcode.react'
import { getApiBaseUrlWithoutApi } from '../../config/api'
import 'primeicons/primeicons.css'

export default function PaymentPage() {
  const { theme } = useTheme()
  const { isAuthenticated, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const courseId = searchParams.get('courseId')
  const [course, setCourse] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const userEmail = user?.email || ''

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true)
        if (courseId) {
          const coursesData = await coursesAPI.getPublicCourses({ type: 'online' })
          const coursesList = Array.isArray(coursesData)
            ? coursesData
            : Array.isArray(coursesData?.results)
              ? coursesData.results
              : Array.isArray(coursesData?.data)
                ? coursesData.data
                : []

          const foundCourse = coursesList.find((c: any) => c.id === Number(courseId))

          if (foundCourse) {
            const API_BASE_URL = getApiBaseUrlWithoutApi()
            
            // Lấy hình ảnh khóa học từ database (API) - ưu tiên các field khác nhau
            let imageUrl = foundCourse.image || foundCourse.thumbnail || foundCourse.banner_image || foundCourse.cover_image || '/images/team-photo-1-BB_Ia2hP.jpg'
            // Xử lý URL hình ảnh từ backend
            if (imageUrl && imageUrl.startsWith('/media/')) {
              imageUrl = `${API_BASE_URL}${imageUrl}`
            } else if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/images/') && imageUrl !== '/images/team-photo-1-BB_Ia2hP.jpg') {
              imageUrl = `${API_BASE_URL}${imageUrl}`
            }
            
            // Lấy QR code từ database nếu có (từ API)
            let qrCodeUrl = foundCourse.payment_qr_code || foundCourse.qr_code || foundCourse.qr_image || null
            if (qrCodeUrl && qrCodeUrl.startsWith('/media/')) {
              qrCodeUrl = `${API_BASE_URL}${qrCodeUrl}`
            } else if (qrCodeUrl && !qrCodeUrl.startsWith('http') && !qrCodeUrl.startsWith('/images/')) {
              qrCodeUrl = `${API_BASE_URL}${qrCodeUrl}`
            }
            
            const finalPrice = (foundCourse.sale_price && foundCourse.sale_price < foundCourse.price) 
              ? foundCourse.sale_price 
              : foundCourse.price || 0
            
            setCourse({
              id: foundCourse.id,
              title: foundCourse.title || foundCourse.name,
              subtitle: foundCourse.subtitle || '',
              price: foundCourse.price || 0,
              sale_price: foundCourse.sale_price || null,
              discount_price: foundCourse.discount_price || null,
              original_price: foundCourse.original_price || foundCourse.price || 0,
              final_price: finalPrice,
              image: imageUrl,
              description: foundCourse.description || '',
              payment_qr_code: qrCodeUrl || null
            })
          } else {
            setToast({ message: 'Không tìm thấy khóa học', type: 'error' })
          }
        }
      } catch (error) {
        setToast({ message: 'Không tìm thấy khóa học', type: 'error' })
      } finally {
        setLoading(false)
      }
    }

    if (courseId) {
      fetchCourse()
    }
  }, [courseId])

  const bankInfo = {
    bankName: 'ACB',
    accountNumber: '17386611',
    accountHolder: 'LE DUY KHANG'
  }

  const getTransferContent = () => {
    if (!course) return ''
    return `${userEmail ? userEmail + ' - ' : ''}${course.title}`
  }

  // Build QR code data cho chuyển khoản từ dữ liệu thực tế
  const getQRCodeValue = () => {
    if (!course) return ''
    const transferContent = getTransferContent()
    const finalPrice = course.final_price || course.price || 0
    
    // Format QR code với thông tin thực tế từ database và bank info
    // Format: Bank|Account|Holder|Amount|Content
    return `${bankInfo.bankName}|${bankInfo.accountNumber}|${bankInfo.accountHolder}|${finalPrice}|${transferContent}`
  }
  
  const getFinalPrice = () => {
    if (!course) return 0
    return course.final_price || course.price || 0
  }

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('vi-VN').format(price) + ' ₫'

  const handleZaloContact = (phone: string) => {
    window.open(`https://zalo.me/${phone}`, '_blank')
  }

  if (loading) {
    return (
      <main className={`${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-100 text-gray-900'} min-h-screen`}>
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải thông tin khóa học...</p>
          </div>
        </div>
      </main>
    )
  }

  if (!course) {
    return (
      <main className={`${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-100 text-gray-900'} min-h-screen`}>
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Không tìm thấy khóa học</p>
            <button
              onClick={() => navigate('/course/online')}
              className="mt-4 px-6 py-2 bg-gold-gradient text-black font-bold rounded-lg"
            >
              Quay lại danh sách khóa học
            </button>
          </div>
        </div>
      </main>
    )
  }

  const originalPrice = course.original_price || course.price
  const hasDiscount = originalPrice && course.final_price && originalPrice > course.final_price

  return (
    <main className={`${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-100 text-gray-900'} min-h-screen`}>
      <Header />
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header Section */}
      <section className={`relative overflow-hidden py-16 md:py-20 px-4 ${theme === 'dark' ? 'bg-gradient-to-b from-black to-gold-900/10' : 'bg-gradient-to-b from-gray-100 to-white'}`}>
        <div className="relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}
          >
            Thanh toán khóa học
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`text-base md:text-lg max-w-2xl mx-auto ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
          >
            Tham gia sớm để trải nghiệm trọn vẹn hệ thống đào tạo online của SaiGon FC
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-6 md:gap-8">
          
          {/* Left Panel - Course Details */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className={`lg:col-span-2 rounded-2xl overflow-hidden border-2 ${theme === 'dark' ? 'bg-gray-900/50 border-gold-500/30' : 'bg-white border-gold-400 shadow-xl'}`}
          >
            {/* Course Image */}
            <div className="relative h-64 md:h-80 overflow-hidden">
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            </div>

            {/* Course Content */}
            <div className={`p-6 md:p-8 space-y-6 ${theme === 'dark' ? 'bg-gray-900/50' : 'bg-white'}`}>
              {/* Course Title */}
              <h2 className={`text-2xl md:text-3xl font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {course.title}
              </h2>

              {/* Course Description - Full với background đen tràn hết khung, có scroll nếu dài */}
              {course.description && (
                <div className={`-mx-6 md:-mx-8 px-6 md:px-8 py-6 md:py-8 ${theme === 'dark' ? 'bg-black' : 'bg-gray-900'}`}>
                  <div className="max-h-64 md:max-h-80 overflow-y-auto custom-scrollbar pr-2">
                    <p className={`text-sm md:text-base leading-relaxed whitespace-pre-line ${theme === 'dark' ? 'text-gray-300' : 'text-gray-300'}`}>
                      {course.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Payment Summary */}
              <div className={`pt-6 border-t ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <span className={`text-base md:text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Tổng thanh toán
                  </span>
                  <div className="flex items-center gap-3">
                    {hasDiscount && (
                      <span className={`text-lg md:text-xl line-through ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        {formatPrice(originalPrice)}
                      </span>
                    )}
                    <span className={`text-2xl md:text-3xl lg:text-4xl font-extrabold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                      {formatPrice(getFinalPrice())}
                    </span>
                  </div>
                </div>
                <p className={`text-sm md:text-base mt-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Giá ưu đãi dành cho thành viên trải nghiệm giai đoạn đầu
                </p>
              </div>

              {/* Features */}
              <div className={`flex flex-wrap gap-4 md:gap-6 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                <div className="flex items-center gap-2">
                  <i className={`pi pi-check-circle text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`} />
                  <span>Cam kết hỗ trợ</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className={`pi pi-check-circle text-lg ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`} />
                  <span>Kích hoạt nhanh</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Panel - Payment Instructions */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className={`rounded-2xl p-6 md:p-8 space-y-6 border-2 ${theme === 'dark' ? 'bg-gray-900/50 border-gold-500/30' : 'bg-white border-gold-400 shadow-xl'}`}
          >
            <h3 className={`text-xl md:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Chuyển khoản
            </h3>

            {/* Bank Details */}
            <div className={`space-y-4 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex justify-between items-center">
                <span>Ngân hàng</span>
                <strong className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {bankInfo.bankName}
                </strong>
              </div>
              <div className="flex justify-between items-center">
                <span>Số tài khoản</span>
                <strong className={`${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                  {bankInfo.accountNumber}
                </strong>
              </div>
              <div className="flex justify-between items-center">
                <span>Chủ tài khoản</span>
                <strong className={`text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {bankInfo.accountHolder}
                </strong>
              </div>
              
              {/* Transfer Content */}
              <div className={`pt-4 border-t ${theme === 'dark' ? 'border-gold-500/30' : 'border-gold-400/30'}`}>
                <p className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  Nội dung CK
                </p>
                <p className={`text-sm font-semibold break-words ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                Email Tài Khoản + {getTransferContent()}
                </p>
              </div>
            </div>

            {/* QR Code */}
            <div className="text-center">
              <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                Quét QR để chuyển khoản nhanh
              </p>
              <div className={`inline-block p-4 rounded-xl border-2 ${theme === 'dark' ? 'bg-gray-800 border-gold-500/30' : 'bg-white border-gold-400'}`}>
                <div className="w-48 h-48 md:w-56 md:h-56 flex items-center justify-center rounded-lg bg-white p-2">
                  {course.payment_qr_code ? (
                    // Nếu có QR code từ database, dùng hình ảnh đó
                    <img
                      src={course.payment_qr_code}
                      alt="QR chuyển khoản"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    // Nếu không có, build QR code từ dữ liệu thực tế
                    <QRCodeSVG
                      value={getQRCodeValue()}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className={`border-2 rounded-xl p-4 text-sm ${theme === 'dark' ? 'bg-gold-500/10 border-gold-500/30 text-gray-300' : 'bg-gold-50 border-gold-400 text-gray-700'}`}>
              Sau khi chuyển khoản, vui lòng gửi <strong>ảnh biên lai</strong> qua Zalo{' '}
              <span className={`font-semibold ${theme === 'dark' ? 'text-gold-400' : 'text-gold-600'}`}>
                0879933020
              </span>{' '}
              để kích hoạt khóa học.
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {/* Nút 1: Đăng ký tài khoản ngay (nếu chưa đăng nhập) */}
              {!isAuthenticated && (
                <button
                  onClick={() => navigate('/register')}
                  className="w-full py-3 rounded-xl bg-gold-gradient text-black text-base font-bold hover:shadow-2xl hover:shadow-gold-500/50 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
                >
                  <i className="pi pi-user-plus text-lg"></i>
                  <span>Đăng ký tài khoản ngay</span>
                </button>
              )}
              
              {/* Nút 2: Chat Zalo xác nhận (luôn hiển thị) */}
              <button
                onClick={() => handleZaloContact('0879933020')}
                className="w-full py-3 rounded-xl bg-blue-600 text-white text-base font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              >
                <i className="pi pi-comments text-lg"></i>
                <span>Chat Zalo xác nhận</span>
              </button>
              
              {/* Nút 3: Quay lại */}
              <button
                onClick={() => navigate(-1)}
                className={`w-full py-3 rounded-xl text-base font-semibold transition ${theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}
              >
                Quay lại
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
